<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Estatisticas de Contatos',
  },
});

const totalContacts = computed(() => props.data?.total_contacts || 0);
const newContacts = computed(() => props.data?.new_contacts || 0);
const returningContacts = computed(() => props.data?.returning_contacts || 0);
const returningRate = computed(() => props.data?.returning_rate || 0);
const avgConversations = computed(
  () => props.data?.avg_conversations_per_contact || 0
);
const topContacts = computed(() => props.data?.top_contacts || []);

const newPercentage = computed(() => {
  if (totalContacts.value === 0) return 50;
  return ((newContacts.value / totalContacts.value) * 100).toFixed(1);
});
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-users text-lg text-n-violet-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Main stats -->
      <div class="grid grid-cols-3 gap-3">
        <div class="text-center p-3 rounded-lg bg-n-violet-3">
          <div class="text-2xl font-bold text-n-violet-11">
            {{ totalContacts }}
          </div>
          <div class="text-xs text-n-slate-11">Total contatos</div>
        </div>
        <div class="text-center p-3 rounded-lg bg-n-blue-3">
          <div class="text-2xl font-bold text-n-blue-11">{{ newContacts }}</div>
          <div class="text-xs text-n-slate-11">Novos</div>
        </div>
        <div class="text-center p-3 rounded-lg bg-n-teal-3">
          <div class="text-2xl font-bold text-n-teal-11">
            {{ returningContacts }}
          </div>
          <div class="text-xs text-n-slate-11">Recorrentes</div>
        </div>
      </div>

      <!-- New vs Returning bar -->
      <div>
        <div class="flex justify-between text-xs text-n-slate-11 mb-1">
          <span>Novos ({{ newPercentage }}%)</span>
          <span>Recorrentes ({{ returningRate }}%)</span>
        </div>
        <div class="flex h-3 rounded-full overflow-hidden bg-n-alpha-1">
          <div
            class="bg-n-blue-9 transition-all duration-300"
            :style="{ width: `${newPercentage}%` }"
          />
          <div
            class="bg-n-teal-9 transition-all duration-300"
            :style="{ width: `${returningRate}%` }"
          />
        </div>
      </div>

      <!-- Average conversations -->
      <div
        class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-1"
      >
        <span class="text-sm text-n-slate-11">Media de conversas por contato</span>
        <span class="text-lg font-bold text-n-slate-12">{{
          avgConversations
        }}</span>
      </div>

      <!-- Top contacts -->
      <div v-if="topContacts.length > 0">
        <div class="flex items-center gap-2 mb-3">
          <span class="i-lucide-trophy text-sm text-n-amber-10" />
          <span class="text-xs font-medium text-n-slate-11 uppercase">Contatos mais ativos</span>
        </div>
        <div class="flex flex-col gap-2 max-h-40 overflow-y-auto">
          <div
            v-for="(contact, index) in topContacts.slice(0, 5)"
            :key="contact.id"
            class="flex items-center gap-3 p-2 rounded-lg hover:bg-n-alpha-1 transition-colors"
          >
            <span class="text-xs font-medium text-n-slate-10 w-4">{{ index + 1 }}.</span>
            <Avatar :name="contact.name" :size="28" rounded-full />
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-n-slate-12 truncate">
                {{ contact.name }}
              </div>
              <div
                v-if="contact.email"
                class="text-xs text-n-slate-10 truncate"
              >
                {{ contact.email }}
              </div>
            </div>
            <span
              class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-blue-3 text-n-blue-11"
            >
              {{ contact.conversation_count }} conv.
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
